import { useState } from "react";
import {
  Calculator, Users, BarChart3, Wrench, UserCheck,
  UserMinus, FolderOpen, PieChart, ArrowLeft, Lock, ExternalLink,
} from "lucide-react";
import { useOutletContext } from "react-router-dom";
import PreciosOcupacion from "../components/tools/PreciosOcupacion";
import ControlLeads from "../components/tools/ControlLeads";
import Metricas from "../components/tools/Metricas";
import SeguimientoClientes from "../components/tools/SeguimientoClientes";
import ControlBajas from "../components/tools/ControlBajas";
import Estadisticas from "../components/tools/Estadisticas";
import ClientesMensuales from "../components/tools/ClientesMensuales";

// A partir del día 22 (hora España, CEST = UTC+2) las herramientas viven en
// Polaris. Se usa un offset explícito para no depender de la zona horaria del
// navegador y para incluir todo el día 22 como "nuevo".
const NEW_STUDENT_CUTOFF = new Date("2026-07-22T00:00:00+02:00");

const CLIENTES_TOOLS = [
  {
    id: "control-leads",
    name: "Control de Leads",
    description: "Gestiona y haz seguimiento de tus potenciales clientes",
    icon: Users,
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    id: "seguimiento-clientes",
    name: "Seguimiento Clientes",
    description: "Planning de seguimiento inicial para clientes nuevos",
    icon: UserCheck,
    color: "bg-teal-50 text-teal-600",
  },
  {
    id: "control-bajas",
    name: "Control de Bajas",
    description: "Gestiona las bajas y su seguimiento para recuperación",
    icon: UserMinus,
    color: "bg-red-50 text-red-600",
  },
  {
    id: "clientes-mensuales",
    name: "Clientes Mensuales",
    description: "Registra el nº de clientes activos cada mes",
    icon: Users,
    color: "bg-blue-50 text-blue-600",
  },
  {
    id: "estadisticas",
    name: "Estadísticas",
    description: "Análisis y gráficos de leads, clientes y bajas",
    icon: PieChart,
    color: "bg-indigo-50 text-indigo-600",
  },
];

const MAIN_TOOLS = [
  {
    id: "precios-ocupacion",
    name: "Precios - Ocupación 100",
    description: "Simulador de rentabilidad para analizar y optimizar los números de tu centro",
    icon: Calculator,
    color: "bg-brand-light text-brand",
  },
  {
    id: "control-clientes",
    name: "Control de Clientes",
    description: "Leads, seguimiento, bajas y estadísticas de tu cartera de clientes",
    icon: FolderOpen,
    color: "bg-amber-50 text-amber-600",
    isFolder: true,
  },
  {
    id: "metricas",
    name: "Métricas",
    description: "Visualiza los indicadores clave de tu negocio",
    icon: BarChart3,
    color: "bg-purple-50 text-purple-600",
  },
];

function ToolCard({ tool, locked, onClick }) {
  const Icon = tool.icon;

  if (locked) {
    return (
      <div className="relative p-6 bg-white rounded-xl border border-gray-200 text-left">
        <div className={`w-12 h-12 rounded-xl ${tool.color} flex items-center justify-center mb-4 opacity-50`}>
          <Icon size={24} />
        </div>
        <h3 className="font-semibold text-gray-500 mb-1">{tool.name}</h3>
        <p className="text-sm text-gray-400 line-clamp-2">{tool.description}</p>
        <div className="mt-4 flex items-center gap-1.5 bg-brand-light/60 text-brand rounded-full px-2.5 py-1 w-fit text-xs font-medium">
          <Lock size={11} />
          <span>Completa Tu punto de partida para desbloquear</span>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      className="relative p-6 bg-white rounded-xl border border-gray-200 text-left transition-all hover:border-brand hover:shadow-lg hover:-translate-y-1 cursor-pointer"
    >
      <div className={`w-12 h-12 rounded-xl ${tool.color} flex items-center justify-center mb-4`}>
        <Icon size={24} />
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">{tool.name}</h3>
      <p className="text-sm text-gray-500 line-clamp-2">{tool.description}</p>
      {tool.isFolder && (
        <div className="absolute top-4 right-4">
          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
            4 herramientas
          </span>
        </div>
      )}
    </button>
  );
}

function MainToolsGrid({ metricsUnlocked, onSelectTool, onOpenFolder }) {
  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-brand-light flex items-center justify-center mx-auto mb-4">
          <Wrench size={32} className="text-brand" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Herramientas</h2>
        <p className="text-gray-500">Recursos para gestionar y optimizar tu centro</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
        {MAIN_TOOLS.map((tool) => {
          const locked = (tool.id === "control-clientes" || tool.id === "metricas")
            && !metricsUnlocked;

          return (
            <ToolCard
              key={tool.id}
              tool={tool}
              locked={locked}
              onClick={() => tool.isFolder ? onOpenFolder(tool.id) : onSelectTool(tool.id)}
            />
          );
        })}
      </div>
    </div>
  );
}

function ClientesFolderGrid({ onSelectTool, onBack }) {
  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-3 mb-8">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
          <FolderOpen size={24} className="text-amber-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Control de Clientes</h2>
          <p className="text-sm text-gray-500">Gestión completa de tu cartera de clientes</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
        {CLIENTES_TOOLS.map((tool) => (
          <ToolCard
            key={tool.id}
            tool={tool}
            onClick={() => onSelectTool(tool.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default function Tools() {
  const { student } = useOutletContext();

  const [activeTool, setActiveTool] = useState(null);
  const [activeFolder, setActiveFolder] = useState(null);

  const metricsUnlocked = student?.tools_metrics_unlocked ?? false;

  const isNewStudent = !!student?.created_at
    && new Date(student.created_at) >= NEW_STUDENT_CUTOFF;

  if (isNewStudent) {
    return (
      <div className="animate-fade-in">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-brand-light flex items-center justify-center mx-auto mb-4">
            <Wrench size={32} className="text-brand" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Herramientas</h2>
        </div>

        <div className="max-w-md mx-auto p-6 bg-white rounded-xl border border-gray-200 text-center">
          <div className="w-12 h-12 rounded-xl bg-brand-light text-brand flex items-center justify-center mx-auto mb-4">
            <ExternalLink size={24} />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Herramientas en Polaris</h3>
          <p className="text-sm text-gray-500">
            Esta sección ahora vive en Polaris. Accede desde tu cuenta de Polaris para usar estas herramientas.
          </p>
        </div>
      </div>
    );
  }

  const handleBackToMain = () => {
    setActiveTool(null);
    setActiveFolder(null);
  };

  const handleBackToFolder = () => {
    setActiveTool(null);
  };

  if (activeTool === "precios-ocupacion") {
    return <PreciosOcupacion onBack={handleBackToMain} />;
  }

  if (activeTool === "metricas") {
    return <Metricas onBack={handleBackToMain} />;
  }

  if (activeTool === "control-leads") {
    return <ControlLeads onBack={handleBackToFolder} />;
  }

  if (activeTool === "seguimiento-clientes") {
    return <SeguimientoClientes onBack={handleBackToFolder} />;
  }

  if (activeTool === "control-bajas") {
    return <ControlBajas onBack={handleBackToFolder} />;
  }

  if (activeTool === "clientes-mensuales") {
    return <ClientesMensuales onBack={handleBackToFolder} />;
  }

  if (activeTool === "estadisticas") {
    return <Estadisticas onBack={handleBackToFolder} />;
  }

  if (activeFolder === "control-clientes") {
    return (
      <ClientesFolderGrid
        onSelectTool={setActiveTool}
        onBack={handleBackToMain}
      />
    );
  }

  return (
    <MainToolsGrid
      metricsUnlocked={metricsUnlocked}
      onSelectTool={setActiveTool}
      onOpenFolder={setActiveFolder}
    />
  );
}
